// El editor con formato de la respuesta (D98) muestra el HTML de markdownAHtml y la persona lo edita ahi mismo. Esto lo
// devuelve a Markdown para que el texto siga siendo uno solo: el borrador, la nota interna y el envio no cambian.

const BLOQUES = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'UL', 'OL', 'TABLE', 'PRE', 'BLOCKQUOTE']);

function enLinea(n: Node): string {
  if (n.nodeType === Node.TEXT_NODE) return (n.textContent ?? '').replace(/ /g, ' ');
  if (!(n instanceof HTMLElement)) return '';
  const dentro = () => Array.from(n.childNodes).map(enLinea).join('');
  switch (n.tagName) {
    case 'BR':
      return '\n';
    case 'STRONG':
    case 'B': {
      const t = dentro();
      return t.trim() ? `**${t.trim()}**` : t;
    }
    case 'EM':
    case 'I': {
      const t = dentro();
      return t.trim() ? `*${t.trim()}*` : t;
    }
    case 'CODE':
      return `\`${n.textContent ?? ''}\``;
    case 'A': {
      const t = dentro();
      const href = n.getAttribute('href') ?? '';
      return !href || href === t || href === `mailto:${t}` ? t : `[${t}](${href})`;
    }
    default:
      // Un bloque dentro de una linea (Chrome mete <div> al dar Enter) es un salto de linea.
      return BLOQUES.has(n.tagName) ? `\n${dentro()}\n` : dentro();
  }
}

const limpio = (s: string) => s.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
const celda = (c: Element) => limpio(enLinea(c)).replace(/\n/g, ' ').replace(/\|/g, '/');

function bloque(n: Node): string {
  if (!(n instanceof HTMLElement)) return enLinea(n);
  const t = n.tagName;
  if (t === 'UL' || t === 'OL')
    return Array.from(n.children)
      .filter((li) => li.tagName === 'LI')
      .map((li, i) => `${t === 'OL' ? `${i + 1}.` : '-'} ${limpio(enLinea(li)).replace(/\n/g, ' ')}`)
      .join('\n');
  if (t === 'TABLE') {
    const filas = Array.from(n.querySelectorAll('tr')).map((tr) => Array.from(tr.children).map(celda));
    if (!filas.length) return '';
    const ancho = Math.max(...filas.map((f) => f.length));
    const fila = (f: string[]) => `| ${Array.from({ length: ancho }, (_, i) => f[i] ?? '').join(' | ')} |`;
    return [fila(filas[0]), `| ${Array(ancho).fill('---').join(' | ')} |`, ...filas.slice(1).map(fila)].join('\n');
  }
  if (t === 'PRE') return `\`\`\`\n${n.textContent ?? ''}\n\`\`\``;
  if (/^H[1-4]$/.test(t)) return `## ${limpio(enLinea(n)).replace(/\n/g, ' ')}`;
  // Titulo como lo escribe markdownAHtml: un parrafo entero en negrita y mas grande.
  if (t === 'P' && n.style.fontWeight === 'bold' && n.style.fontSize) return `## ${limpio(n.textContent ?? '')}`;
  return enLinea(n);
}

/** Markdown del contenido de un editor (contentEditable). Bloques separados por una linea en blanco. */
export function htmlAMarkdown(raiz: HTMLElement): string {
  const partes: string[] = [];
  let suelto = '';
  const soltar = () => {
    if (suelto.trim()) partes.push(limpio(suelto));
    suelto = '';
  };
  for (const n of Array.from(raiz.childNodes)) {
    if (n instanceof HTMLElement && BLOQUES.has(n.tagName)) {
      soltar();
      const b = limpio(bloque(n));
      partes.push(b);
    } else suelto += enLinea(n);
  }
  soltar();
  // Un parrafo vacio (Enter dos veces) deja un hueco: se conserva como una sola linea en blanco.
  return partes.join('\n\n').replace(/\n{3,}/g, '\n\n').trim();
}
